import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useAktion, useLaden } from '../../hooks'
import Meldungen from '../../komponenten/Meldungen'
import { datum, stueck, zeitpunkt } from '../../format'
import { notierungLaufPfad } from '../../pfade'
import { NOTIERUNG_LAUFQUELLE, NOTIERUNG_LAUFSTATUS } from '../../texte'
import type { NotierungLaufStatus } from '../../typen'

const ANNEHMEN = '.pdf,.png,.jpg,.jpeg,.eml,.txt'
const HOECHSTENS_DATEIEN = 10

const FILTER: { wert: NotierungLaufStatus | ''; titel: string }[] = [
  { wert: '', titel: 'alle' },
  { wert: 'pruefen', titel: 'zu prüfen' },
  { wert: 'uebernommen', titel: 'übernommen' },
  { wert: 'verworfen', titel: 'verworfen' },
  { wert: 'zurueckgenommen', titel: 'zurückgenommen' },
  { wert: 'fehler', titel: 'nicht lesbar' },
]

/**
 * Importe der Rohstoffnotierungen: oben die Ablage für das, was nicht per Mail kam (PDF, Bild der
 * Bunge-Czech-Mail, gespeicherte .eml) und ein Feld für eingefügten Mailtext (Pöhner-Angebot) - ein
 * Klick macht daraus einen Lauf. Darunter alle Läufe; ein Lauf wird nur dann ohne Klick übernommen,
 * wenn jede Position einer Spalte sicher zugeordnet oder begründet ausgelassen ist.
 */
export default function ImporteSeite() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<NotierungLaufStatus | ''>('')
  const { daten: laeufe, fehler: ladeFehler } = useLaden(() => notierungenApi.laeufe(filter || undefined), [filter])
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = useAktion()
  const [dateien, setDateien] = useState<File[]>([])
  const [text, setText] = useState('')
  const [ziehen, setZiehen] = useState(false)
  const auswahl = useRef<HTMLInputElement>(null)

  function dazu(neue: FileList | File[]) {
    const liste = [...dateien, ...Array.from(neue)]
    const erlaubt = liste.filter((d) => /\.(pdf|png|jpe?g|eml|txt)$/i.test(d.name))
    if (erlaubt.length < liste.length) setFehler('Nur PDF, PNG, JPG, .eml und .txt werden gelesen - die übrigen Dateien sind weggelassen.')
    if (erlaubt.length > HOECHSTENS_DATEIEN) setFehler(`Höchstens ${HOECHSTENS_DATEIEN} Dateien je Lauf - die übrigen sind weggelassen.`)
    setDateien(erlaubt.slice(0, HOECHSTENS_DATEIEN))
  }

  function einlesen() {
    void ausfuehren(async () => {
      const lauf = dateien.length > 0 ? await notierungenApi.hochladen(dateien, text) : await notierungenApi.text(text)
      setDateien([])
      setText('')
      navigate(notierungLaufPfad(lauf.id))
    })
  }

  function excelEinlesen(datei: File) {
    void ausfuehren(async () => {
      const a = await notierungenApi.excel(datei)
      setErfolg(
        `Altbestand eingelesen: ${a.blaetter} Blätter, ${stueck(a.werte)} Werte, ${a.quellen_neu} neue Spalten, ${a.preisspannen} Preisspannen (Lauf ${a.lauf_id}).`,
      )
    })
  }

  const bereit = dateien.length > 0 || text.trim() !== ''

  return (
    <>
      <h2>Importe</h2>
      <div className="karte nz-ablage-karte">
        <div
          className={`nz-ablage${ziehen ? ' ziehen' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => auswahl.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') auswahl.current?.click()
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setZiehen(true)
          }}
          onDragLeave={() => setZiehen(false)}
          onDrop={(e) => {
            e.preventDefault()
            setZiehen(false)
            dazu(e.dataTransfer.files)
          }}
        >
          <b>Notierungen hierher ziehen oder klicken</b>
          <span className="hinweis">PDF, Bild (PNG, JPG), gespeicherte Mail (.eml) oder Text - bis zu {HOECHSTENS_DATEIEN} Dateien, die zusammen ein Lauf werden.</span>
          <input
            ref={auswahl}
            type="file"
            multiple
            accept={ANNEHMEN}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) dazu(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
        {dateien.length > 0 && (
          <ul className="nz-dateiliste">
            {dateien.map((d, i) => (
              <li key={`${d.name}-${i}`}>
                {d.name} <span className="hinweis">({stueck(Math.ceil(d.size / 1024))} KB)</span>{' '}
                <button className="leise" onClick={() => setDateien(dateien.filter((_, j) => j !== i))}>
                  entfernen
                </button>
              </li>
            ))}
          </ul>
        )}
        <label className="feld nz-mailtext">
          <span className="hinweis">Mailtext einfügen (z. B. ein Angebot von Pöhner) - auch zusammen mit Dateien, etwa die Regel aus der Weiterleitung</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Guten Tag, wir bieten an: raff. Rapsöl, Lieferung Jan-März 2027, 1.350,00 €/t CPT Gera …'}
          />
        </label>
        <div className="leiste" style={{ marginBottom: 0 }}>
          <button className="haupt" disabled={!bereit || arbeitet} onClick={einlesen}>
            {arbeitet ? 'Wird gelesen…' : 'Einlesen'}
          </button>
          <span className="hinweis">
            Mails an othuena@intsode.com und othuena@vai.com.de kommen von selbst. Was hier eingelesen wird, steht danach als Lauf
            zur Prüfung - übernommen wird erst mit Klick, außer alle Prüfungen bestehen.
          </span>
          <label className="knopf nz-excel" title="Die drei alten Mappen (Rapsöl, Palmöl/Palmstearin, Umesterung/Kokos) als Altbestand übernehmen">
            Excel-Mappe (Altbestand)
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              disabled={arbeitet}
              onChange={(e) => {
                const d = e.target.files?.[0]
                if (d) excelEinlesen(d)
                e.target.value = ''
              }}
            />
          </label>
        </div>
      </div>

      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />

      <div className="leiste">
        <h3 style={{ margin: 0 }}>Läufe</h3>
        <label className="hinweis">
          Stand{' '}
          <select value={filter} onChange={(e) => setFilter(e.target.value as NotierungLaufStatus | '')}>
            {FILTER.map((f) => (
              <option key={f.wert} value={f.wert}>
                {f.titel}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="tabellenrahmen">
        <table className="nz-laeufe">
          <thead>
            <tr>
              <th>Angelegt</th>
              <th>Woher</th>
              <th>Lieferant · Dokument</th>
              <th>Notiert am</th>
              <th className="zahl">Werte</th>
              <th>Stand</th>
              <th>Hinweis</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(laeufe ?? []).map((l) => (
              <tr key={l.id} onDoubleClick={() => navigate(notierungLaufPfad(l.id))}>
                <td>{zeitpunkt(l.angelegt_am)}</td>
                <td>{NOTIERUNG_LAUFQUELLE[l.quelle] ?? l.quelle}</td>
                <td>
                  <b>{l.lieferant_text ?? 'Lieferant unbekannt'}</b>
                  {l.dateiname && <div className="hinweis">{l.dateiname}</div>}
                </td>
                <td>{l.notiert_am ? datum(l.notiert_am) : <span className="hinweis">unbekannt</span>}</td>
                <td className="zahl">
                  {l.werte_anzahl}
                  {l.offen_anzahl > 0 && <div className="hinweis">{l.offen_anzahl} offen</div>}
                </td>
                <td>
                  <span className={`status ${NOTIERUNG_LAUFSTATUS[l.status].klasse}`}>{NOTIERUNG_LAUFSTATUS[l.status].text}</span>
                  {l.automatisch && <div className="hinweis">ohne Klick übernommen</div>}
                  {!l.automatisch && l.entschieden_von && <div className="hinweis">von {l.entschieden_von}</div>}
                </td>
                <td className="nz-hinweis-zelle">
                  {l.hinweise[0] ?? ''}
                  {l.hinweise.length > 1 && <span className="hinweis"> (+{l.hinweise.length - 1})</span>}
                </td>
                <td>
                  <button onClick={() => navigate(notierungLaufPfad(l.id))}>{l.status === 'pruefen' ? 'Prüfen' : 'Ansehen'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {laeufe !== null && laeufe.length === 0 && (
          <p className="leer">{filter ? 'Kein Lauf in diesem Stand.' : 'Noch keine Notierung eingelesen. Oben eine Datei ablegen oder Mailtext einfügen.'}</p>
        )}
      </div>
    </>
  )
}
